/**
 * Integrytas Group - Base App (MOBILE)
 * Page: CMS.PAGE.LIST — mobile-native.
 *
 * Touch-first counterpart of src/pages/cms/CmsPageListPage.tsx. A DISTINCT
 * presentation (dense card list + status facet chips + bottom-sheet detail +
 * a small edit sheet) over the SAME data/logic layer:
 *   - reads via apiDb (cms_page_master + cms_page_master_tl for the localized
 *     title/slug, plus cms_page_type / cms_page_status + their _tl lookups so no
 *     raw code ever reaches render — §17.B4),
 *   - the status / active / sort edit routes through the Common Routine
 *     (routedUpdate) — §2 single write path.
 * It never re-renders the desktop grid; §16. Element/translation/security tabs
 * stay on the desktop; the mobile sheet edits the page's lifecycle scalars.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, FileText, X, SquarePen } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { useFormatDate, useFormatDateTime } from '../../lib/format_date';
import { routedUpdate } from '../../lib/routed_writes';
import {
  MobilePageHeader, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips,
  MobileStatusPill, type MobilePillTone, type MobileLeadTone,
} from '../components';
import { MobileRelatedChildren } from '../components/MobileRelatedChildren';
import type { AuthSession } from '../../types/auth';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'CMS.PAGE.LIST';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MobilePageRow {
  page_id: string;
  internal_code: string;
  page_type_code: string;
  status_code: string;
  parent_page_id: string | null;
  sort_order: number;
  published_at: string | null;
  is_active: 'Y' | 'N';
  created_at: string | null;
  updated_at: string | null;
  page_title: string;
  slug: string;
}

interface StatusOption {
  status_code: string;
  name: string;
  sort_order: number;
}

interface CmsTlRow {
  language_id: string;
  display_text: string;
}

// Active-status facet dimension — independent of the page status_code facet
// (named ActiveFacet since statusFacet already denotes the status_code chips).
type ActiveFacet = 'all' | 'active' | 'inactive';

// Status code → status-pill / lead tone. Codes are seeded lookups; unknown
// codes fall back to a neutral treatment rather than surfacing a raw literal.
const STATUS_TONE: Record<string, MobilePillTone> = {
  published: 'ok',
  review: 'wait',
  draft: 'info',
  archived: 'no',
};
const STATUS_LEAD_TONE: Record<string, MobileLeadTone> = {
  published: 'green',
  review: 'amber',
  draft: 'blue',
  archived: 'slate',
};
const pillTone = (code: string): MobilePillTone => STATUS_TONE[code] ?? 'info';
const leadTone = (code: string): MobileLeadTone => STATUS_LEAD_TONE[code] ?? 'slate';

// Resolve a lookup's _tl rows to the active-locale name (falls back to EN_US,
// then any row, then the code) — §8 / §17.B4.
function resolveName(tls: CmsTlRow[] | undefined, locale: string, fallback: string): string {
  const rows = tls ?? [];
  const exact = rows.find((tl) => tl.language_id === locale);
  const fb = rows.find((tl) => tl.language_id === 'EN_US') ?? rows[0];
  return exact?.display_text ?? fb?.display_text ?? fallback;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function MobileCmsPageListPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const fmtDate = useFormatDate();
  const fmtDateTime = useFormatDateTime();

  const [rows, setRows] = useState<MobilePageRow[]>([]);
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);
  const [statusNames, setStatusNames] = useState<Record<string, string>>({});
  const [typeNames, setTypeNames] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<string>('all');
  const [activeFacet, setActiveFacet] = useState<ActiveFacet>('active');
  const [selected, setSelected] = useState<MobilePageRow | null>(null);
  const [editing, setEditing] = useState<MobilePageRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data: rawPages, error: pErr } = await apiDb
        .from('cms_page_master')
        .select(`
          page_id, internal_code, page_type_code, status_code,
          parent_page_id, sort_order, published_at, is_active,
          created_at, updated_at,
          cms_page_master_tl!left ( page_title, slug, language_id )
        `)
        .order('sort_order');
      if (pErr) throw pErr;

      type TlJoin = { page_title?: string; slug?: string; language_id: string };
      type RawPage = Omit<MobilePageRow, 'page_title' | 'slug'> & { cms_page_master_tl?: TlJoin[] };
      const flat: MobilePageRow[] = ((rawPages ?? []) as RawPage[]).map((r) => {
        const tls = r.cms_page_master_tl ?? [];
        const tl = tls.find((x) => x.language_id === activeLocale)
          ?? tls.find((x) => x.language_id === 'EN_US')
          ?? tls[0];
        return {
          page_id: String(r.page_id),
          internal_code: r.internal_code,
          page_type_code: r.page_type_code,
          status_code: r.status_code,
          parent_page_id: r.parent_page_id ? String(r.parent_page_id) : null,
          sort_order: r.sort_order ?? 0,
          published_at: r.published_at,
          is_active: r.is_active,
          created_at: r.created_at,
          updated_at: r.updated_at,
          page_title: tl?.page_title ?? r.internal_code,
          slug: tl?.slug ?? '',
        };
      });
      setRows(flat);

      // Status lookup + localized names (drives the facet chips and pills).
      const { data: rawStatuses, error: sErr } = await apiDb
        .from('cms_page_status')
        .select('status_code, sort_order, cms_page_status_tl ( language_id, display_text )')
        .order('sort_order');
      if (sErr) throw sErr;
      type RawStatus = { status_code: string; sort_order: number; cms_page_status_tl?: CmsTlRow[] };
      const sNames: Record<string, string> = {};
      const sOpts: StatusOption[] = ((rawStatuses ?? []) as RawStatus[]).map((s) => {
        const name = resolveName(s.cms_page_status_tl, activeLocale, s.status_code);
        sNames[s.status_code] = name;
        return { status_code: s.status_code, name, sort_order: s.sort_order ?? 0 };
      });
      setStatusOptions(sOpts);
      setStatusNames(sNames);

      // Page-type localized names (detail view — never the raw code).
      const { data: rawTypes, error: tErr } = await apiDb
        .from('cms_page_type')
        .select('page_type_code, cms_page_type_tl ( language_id, display_text )')
        .order('sort_order');
      if (tErr) throw tErr;
      type RawType = { page_type_code: string; cms_page_type_tl?: CmsTlRow[] };
      const tNames: Record<string, string> = {};
      for (const ty of (rawTypes ?? []) as RawType[]) {
        tNames[ty.page_type_code] = resolveName(ty.cms_page_type_tl, activeLocale, ty.page_type_code);
      }
      setTypeNames(tNames);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (titles/labels follow it).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [activeLocale]);

  const statusLabel = (code: string) => statusNames[code] ?? code;
  const typeLabel = (code: string) => typeNames[code] ?? code;
  const titleById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const r of rows) map[r.page_id] = r.page_title;
    return map;
  }, [rows]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFacet !== 'all' && r.status_code !== statusFacet) return false;
      if (activeFacet === 'active' && r.is_active !== 'Y') return false;
      if (activeFacet === 'inactive' && r.is_active !== 'N') return false;
      if (!q) return true;
      return (
        r.page_title.toLowerCase().includes(q) ||
        r.internal_code.toLowerCase().includes(q) ||
        r.slug.toLowerCase().includes(q)
      );
    });
  }, [rows, filter, statusFacet, activeFacet]);

  const facets: { key: string; label: string }[] = [
    { key: 'all', label: t('mobile.cms_pages.all', 'All') },
    ...statusOptions.map((s) => ({ key: s.status_code, label: s.name })),
  ];

  const activeFacets: { key: ActiveFacet; label: string }[] = [
    { key: 'all', label: t('mobile.cms_pages.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const parentLabel = (r: MobilePageRow) =>
    r.parent_page_id ? (titleById[r.parent_page_id] ?? t('mobile.cms_pages.unknown_parent', 'Unknown'))
      : t('mobile.cms_pages.no_parent', 'Top level');

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.cms_pages.title', 'Pages')}
        description={t('mobile.cms_pages.description', 'Content pages, their type, status and publication state.')}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.cms_pages.search', 'Search pages')}
          aria-label={t('mobile.cms_pages.search', 'Search pages')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v)}
      />

      <MobileFilterChips
        chips={activeFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => activeFacet === v}
        onSelect={(v) => setActiveFacet(v as ActiveFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.cms_pages.none', 'No pages match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.page_id}
              lead={<FileText />}
              leadTone={leadTone(r.status_code)}
              title={r.page_title}
              subtitle={`${r.internal_code} · ${typeLabel(r.page_type_code)}`}
              trailing={
                <>
                  <MobileStatusPill tone={pillTone(r.status_code)}>{statusLabel(r.status_code)}</MobileStatusPill>
                  <MobileStatusPill tone={r.is_active === 'Y' ? 'ok' : 'no'}>
                    {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                </>
              }
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.cms_pages.detail', 'Page detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><FileText size={20} /></span>
              <span>
                <div className="m-nm">{selected.page_title}</div>
                <div className="m-em">{selected.internal_code}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.cms_pages.status', 'Status')}
                value={<MobileStatusPill tone={pillTone(selected.status_code)}>{statusLabel(selected.status_code)}</MobileStatusPill>}
              />
              <MobileDetailRow label={t('mobile.cms_pages.type', 'Type')} value={typeLabel(selected.page_type_code)} />
              <MobileDetailRow label={t('mobile.cms_pages.slug', 'Slug')} value={selected.slug || t('common.none', 'None')} />
              <MobileDetailRow label={t('mobile.cms_pages.parent', 'Parent')} value={parentLabel(selected)} />
              <MobileDetailRow label={t('mobile.cms_pages.sort_order', 'Sort order')} value={selected.sort_order} />
              <MobileDetailRow
                label={t('mobile.cms_pages.active', 'Active')}
                value={
                  <MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>
                    {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow
                label={t('mobile.cms_pages.published_at', 'Published')}
                value={selected.published_at ? fmtDate(selected.published_at) : t('common.none', 'None')}
              />
              <MobileDetailRow
                label={t('mobile.cms_pages.updated_at', 'Updated')}
                value={selected.updated_at ? fmtDateTime(selected.updated_at) : '—'}
              />
            </MobileContentCard>
            {/* §17.B13 — the row's related records, stacked one group per block.
                Sub-pages come from the already-loaded rows; no extra fetch. */}
            <MobileContentCard>
              <MobileRelatedChildren
                session={session}
                groups={[
                  {
                    key: 'subpages',
                    table: 'csm_page_master', pkColumn: 'page_id',
                    title: t('mobile.cms_pages.related.subpages', 'Sub-pages'),
                    items: rows
                      .filter(r => r.parent_page_id === selected.page_id)
                      .map(r => ({ id: r.page_id, label: r.page_title || r.internal_code, sub: r.internal_code })),
                  },
                ]}
              />
            </MobileContentCard>
            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className="m-btn m-btn-primary"
                onClick={() => { setEditing(selected); setSelected(null); }}
              >
                <SquarePen size={16} />
                {t('mobile.cms_pages.edit', 'Edit')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      <MobileCmsPageEditSheet
        session={session}
        page={editing}
        statusOptions={statusOptions}
        busy={actionBusy}
        setBusy={setActionBusy}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void reload(); }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Edit sheet — status / active / sort, saved via the Common Routine (§2)
// ---------------------------------------------------------------------------

interface MobileCmsPageEditSheetProps {
  session: AuthSession;
  page: MobilePageRow | null;
  statusOptions: StatusOption[];
  busy: boolean;
  setBusy: (v: boolean) => void;
  onClose: () => void;
  onSaved: () => void;
}

function MobileCmsPageEditSheet({ session, page, statusOptions, busy, setBusy, onClose, onSaved }: MobileCmsPageEditSheetProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState('draft');
  const [active, setActive] = useState(true);
  const [sortOrder, setSortOrder] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Seed the form whenever a different page is opened.
  useEffect(() => {
    if (!page) return;
    setStatus(page.status_code);
    setActive(page.is_active === 'Y');
    setSortOrder(page.sort_order);
    setError(null);
  }, [page]);

  const save = async () => {
    if (!page) return;
    setBusy(true);
    setError(null);
    // Stamp published_at when moving to published (keep the existing stamp if set),
    // mirroring the desktop lifecycle rule. §2: route through the Common Routine.
    const publishedAt = status === 'published'
      ? (page.published_at ?? new Date().toISOString())
      : page.published_at;
    const { error: e } = await routedUpdate(session, 'cms_page_master', 'page_id', page.page_id, {
      status_code: status,
      is_active: active ? 'Y' : 'N',
      sort_order: sortOrder,
      published_at: publishedAt,
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const open = page !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.cms_pages.edit', 'Edit page')}>
        <div className="m-grabber" />
        {page && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><FileText size={20} /></span>
              <span>
                <div className="m-nm">{page.page_title}</div>
                <div className="m-em">{page.internal_code}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label htmlFor="m-cms-status">{t('mobile.cms_pages.status', 'Status')}</label>
                <select id="m-cms-status" className="m-sel" value={status} onChange={(e) => setStatus(e.target.value)}>
                  {statusOptions.map((s) => <option key={s.status_code} value={s.status_code}>{s.name}</option>)}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-cms-sort">{t('mobile.cms_pages.sort_order', 'Sort order')}</label>
                <input
                  id="m-cms-sort"
                  type="number"
                  className="m-inp"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(Number(e.target.value))}
                />
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('common.boolean.active', 'Active')}</div>
                  <div className="m-formrow-s">{t('mobile.cms_pages.active_hint', 'Page is available')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  className={'m-switch ' + (active ? 'm-on' : 'm-off')}
                  onClick={() => setActive((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
